import { TaskWithDetails } from './taskService';
import { ComplianceTemplate } from '@/firebase/complianceService';
import { calculateRiskWeight } from '@/firebase/complianceService';

export interface TaskScore {
  taskId: string;
  taskName: string;
  locationId: string;
  locationName: string;
  status: string;
  statusWeight: number;
  minStatusWeight: number;
  riskLevel: string;
  riskWeight: number;
  taskScore: number;
  maxPossibleScore: number;
  minContribution: number;
  isDueSoon: boolean;
}

export interface LocationScore {
  locationId: string;
  locationName: string;
  totalTaskScore: number;
  totalMaxPossibleScore: number;
  totalMinPossibleScore: number;
  completedScore: number;
  openActualScore: number;
  openMinPossibleScore: number;
  openMaxPossibleScore: number;
  score: number; // Percentage score
  taskCount: number;
  taskScores: TaskScore[];
}

export interface OrganizationScore {
  organizationId: string;
  totalTaskScore: number;
  totalMaxPossibleScore: number;
  totalMinPossibleScore: number;
  completedScore: number;
  openActualScore: number;
  openMinPossibleScore: number;
  openMaxPossibleScore: number;
  score: number; // Percentage score
  locationScores: LocationScore[];
}

/**
 * Calculate status weight based on task status and due date
 * - Completed On Time: +1.0
 * - Pending Review: +0.5
 * - Due Soon (Next 30 Days): -0.3 (pending tasks count against score)
 * - Overdue: -1.0
 * - Pending tasks more than 30 days away: excluded from calculation entirely
 */
export function calculateStatusWeight(task: TaskWithDetails): { weight: number; isDueSoon: boolean } {
  const now = new Date();
  const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
  const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Check if task is due soon (within next 30 days and still pending)
  // These tasks are included in calculation with a negative weight to penalize incomplete tasks
  const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 30 && task.status === 'pending';
  
  if (isDueSoon) {
    // Give pending tasks due within 30 days a negative weight to count against score
    // This prevents 100% score when all tasks are pending
    return { weight: -0.3, isDueSoon: true };
  }
  
  switch (task.status) {
    case 'completed':
      return { weight: 1.0, isDueSoon: false };
    case 'pending_review':
      return { weight: 0.5, isDueSoon: false };
    case 'overdue':
      return { weight: -1.0, isDueSoon: false };
    case 'pending':
      // Pending tasks that are past due date are treated as overdue
      if (dueDate < now) {
        return { weight: -1.0, isDueSoon: false };
      }
      // Pending tasks more than 30 days away should not be included in calculation
      // This case should be filtered out before calling this function
      if (daysUntilDue > 30) {
        return { weight: 0, isDueSoon: false };
      }
      // Pending tasks within 30 days but not caught by isDueSoon (shouldn't happen, but handle it)
      return { weight: 0, isDueSoon: false };
    default:
      return { weight: 0, isDueSoon: false };
  }
}

/**
 * Calculate task score: StatusWeight × RiskWeight
 */
export function calculateTaskScore(
  task: TaskWithDetails,
  template: ComplianceTemplate | undefined
): TaskScore {
  const { weight: statusWeight, isDueSoon } = calculateStatusWeight(task);
  const riskWeight = template?.risk_weight ?? calculateRiskWeight(task.riskLevel || 'Medium');
  const minStatusWeight = getMinStatusWeight(task, statusWeight, isDueSoon);
  
  // Task score = StatusWeight × RiskWeight
  const taskScore = statusWeight * riskWeight;
  const minContribution = minStatusWeight * riskWeight;

  // Max possible score = CompletedWeight(1.0) × RiskWeight
  const maxPossibleScore = 1.0 * riskWeight;
  
  return {
    taskId: task.id,
    taskName: task.templateName,
    locationId: task.locationId,
    locationName: task.locationName,
    status: task.status,
    statusWeight,
    minStatusWeight,
    riskLevel: template?.risk_level || task.riskLevel || 'Medium',
    riskWeight,
    taskScore,
    maxPossibleScore,
    minContribution,
    isDueSoon
  };
}

/**
 * Calculate location score from its tasks
 * Only counts tasks that are due within the next 30 days, overdue, completed, or pending review
 * Location Score = Sum(Task Scores) ÷ Sum(Max Possible Task Scores) × 100
 */
export function calculateLocationScore(
  locationId: string,
  locationName: string,
  tasks: TaskWithDetails[],
  templates: ComplianceTemplate[]
): LocationScore {
  const now = new Date();
  const thirtyDaysFromNow = new Date(now);
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  
  // Filter tasks to only include:
  // 1. Tasks due within next 30 days
  // 2. Overdue tasks
  // 3. Completed tasks that were due within next 30 days (only recent/relevant completions)
  // 4. Pending review tasks
  // 5. Exclude custom tasks (tasks without a templateId)
  const locationTasks = tasks.filter(task => {
    if (task.locationId !== locationId) return false;
    
    // Exclude custom tasks (tasks without a templateId)
    if (!task.templateId || task.templateId === null || task.templateId === '') {
      return false;
    }
    
    const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    // Include overdue tasks (always count)
    if (task.status === 'overdue') {
      return true;
    }
    
    // Include pending review tasks (always count)
    if (task.status === 'pending_review') {
      return true;
    }
    
    // Include completed tasks ONLY if they were due within next 30 days
    // This ensures we only count recent/relevant completions, not old ones completed 60+ days ago
    if (task.status === 'completed') {
      // Only count if due date is within next 30 days from now
      // This means the task is still relevant (was due soon when completed or is still upcoming)
      return daysUntilDue >= 0 && daysUntilDue <= 30;
    }
    
    // Include pending tasks due within next 30 days
    if (task.status === 'pending' && daysUntilDue >= 0 && daysUntilDue <= 30) {
      return true;
    }
    
    // Exclude everything else (pending tasks more than 30 days away, old completed tasks, etc.)
    return false;
  });
  
  const taskScores = locationTasks.map(task => {
    const template = templates.find(t => t.id === task.templateId);
    return calculateTaskScore(task, template);
  });
  
  // Include all task scores in calculation (including "due soon" tasks which now have negative weight)
  // This ensures pending tasks due within 30 days count against the score
  // Filter out only tasks more than 30 days away (they shouldn't be in the list, but just in case)
  const countedTaskScores = taskScores.filter(ts => {
    // Include all tasks (including "due soon" tasks which now have weight -0.3)
    // This ensures pending tasks count against the score
    return true;
  });
  
  // Only completed tasks count as completed. pending_review tasks are considered incomplete.
  const completedTaskScores = countedTaskScores.filter(ts => ts.status === 'completed');
  const openTaskScores = countedTaskScores.filter(ts => ts.status !== 'completed');

  const completedScore = completedTaskScores.reduce((sum, ts) => sum + ts.maxPossibleScore, 0);
  const openActualScore = openTaskScores.reduce((sum, ts) => sum + ts.taskScore, 0);
  const openMinPossibleScore = openTaskScores.reduce((sum, ts) => sum + ts.minContribution, 0);
  const openMaxPossibleScore = openTaskScores.reduce((sum, ts) => sum + ts.maxPossibleScore, 0);

  const openRange = openMaxPossibleScore - openMinPossibleScore;
  let normalizedOpenPortion = 0;
  if (openMaxPossibleScore <= 0) {
    normalizedOpenPortion = 0;
  } else if (openRange <= 0) {
    normalizedOpenPortion = openActualScore >= openMaxPossibleScore ? 1 : 0;
  } else {
    normalizedOpenPortion = (openActualScore - openMinPossibleScore) / openRange;
    normalizedOpenPortion = Math.max(0, Math.min(1, normalizedOpenPortion));
  }

  const denominator = completedScore + openMaxPossibleScore;
  const normalizedScore = denominator <= 0
    ? 100
    : ((completedScore + normalizedOpenPortion * openMaxPossibleScore) / denominator) * 100;

  const totalTaskScore = completedScore + openActualScore;
  const totalMaxPossibleScore = completedScore + openMaxPossibleScore;
  const totalMinPossibleScore = completedScore + openMinPossibleScore;
  
  return {
    locationId,
    locationName,
    totalTaskScore,
    totalMaxPossibleScore,
    totalMinPossibleScore,
    completedScore,
    openActualScore,
    openMinPossibleScore,
    openMaxPossibleScore,
    score: normalizedScore,
    taskCount: countedTaskScores.length,
    taskScores
  };
}

/**
 * Calculate organization-wide compliance score
 */
export function calculateOrganizationScore(
  organizationId: string,
  tasks: TaskWithDetails[],
  templates: ComplianceTemplate[]
): OrganizationScore {
  // Group tasks by location
  const locationMap = new Map<string, { name: string; tasks: TaskWithDetails[] }>();
  
  tasks.forEach(task => {
    if (!locationMap.has(task.locationId)) {
      locationMap.set(task.locationId, {
        name: task.locationName,
        tasks: []
      });
    }
    locationMap.get(task.locationId)!.tasks.push(task);
  });
  
  // Calculate score for each location
  const locationScores: LocationScore[] = Array.from(locationMap.entries()).map(([locationId, data]) => {
    return calculateLocationScore(locationId, data.name, data.tasks, templates);
  });
  
  // Aggregate organization score
  const totalTaskScore = locationScores.reduce((sum, ls) => sum + ls.totalTaskScore, 0);
  const totalMaxPossibleScore = locationScores.reduce((sum, ls) => sum + ls.totalMaxPossibleScore, 0);
  const totalMinPossibleScore = locationScores.reduce((sum, ls) => sum + ls.totalMinPossibleScore, 0);
  const completedScore = locationScores.reduce((sum, ls) => sum + ls.completedScore, 0);
  const openActualScore = locationScores.reduce((sum, ls) => sum + ls.openActualScore, 0);
  const openMinPossibleScore = locationScores.reduce((sum, ls) => sum + ls.openMinPossibleScore, 0);
  const openMaxPossibleScore = locationScores.reduce((sum, ls) => sum + ls.openMaxPossibleScore, 0);

  const openRange = openMaxPossibleScore - openMinPossibleScore;
  let normalizedOpenPortion = 0;
  if (openMaxPossibleScore <= 0) {
    normalizedOpenPortion = 0;
  } else if (openRange <= 0) {
    normalizedOpenPortion = openActualScore >= openMaxPossibleScore ? 1 : 0;
  } else {
    normalizedOpenPortion = (openActualScore - openMinPossibleScore) / openRange;
    normalizedOpenPortion = Math.max(0, Math.min(1, normalizedOpenPortion));
  }

  const denominator = completedScore + openMaxPossibleScore;
  const score = denominator <= 0
    ? 100
    : ((completedScore + normalizedOpenPortion * openMaxPossibleScore) / denominator) * 100;
  
  return {
    organizationId,
    totalTaskScore,
    totalMaxPossibleScore,
    totalMinPossibleScore,
    completedScore,
    openActualScore,
    openMinPossibleScore,
    openMaxPossibleScore,
    score,
    locationScores
  };
}

/**
 * Get score color based on percentage
 */
export function getScoreColor(score: number): string {
  if (score >= 90) return 'text-green-600 bg-green-50';
  if (score >= 75) return 'text-green-500 bg-green-50';
  if (score >= 60) return 'text-yellow-600 bg-yellow-50';
  if (score >= 40) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
}

/**
 * Get score badge class for styling
 */
export function getScoreBadgeClass(score: number): string {
  if (score >= 90) return 'bg-green-100 text-green-800 border-green-300';
  if (score >= 75) return 'bg-green-50 text-green-700 border-green-200';
  if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  if (score >= 40) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-red-100 text-red-800 border-red-300';
}

function getMinStatusWeight(task: TaskWithDetails, statusWeight: number, isDueSoon: boolean): number {
  const now = new Date();
  const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);

  if (dueDate < now) {
    return -1.0;
  }
  const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysUntilDue >= 0 && daysUntilDue <= 30) {
    return -0.3;
  }
  if (task.status === 'pending_review') {
    return 0.5;
  }
  if (task.status === 'completed') {
    return 1.0;
  }
  return 0;
}

